import React, { useEffect, useState } from 'react';
import axiosInstance from '../api';
import { useLang } from '../i18n/LanguageContext';

// Public stats panel shown below the Success Stories marquee on the landing
// page. Four tiles at the top (Applications / NOC's Approved / To be Approved / Works Completed),
// dept-wise breakdown below, and a scrolling list of NGO+tank pairs.
const PortalStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { tr } = useLang();

  useEffect(() => {
    axiosInstance
      .get('/applications/public/portal-stats', { silent: true })
      .then((res) => setStats(res.data?.data || null))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats) return null;

  const { applications, noc_approved, ngos } = stats;

  return (
    <section className="w-full bg-white py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-600">{tr('Live Statistics', 'நேரடிப் புள்ளிவிவரங்கள்')}</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
            {tr('Portal at a Glance', 'இணையதளம் ஒரு பார்வையில்')}
          </h2>
          <div className="w-16 h-1 bg-brand-accent mx-auto mt-4"></div>
          <p className="text-slate-500 mt-4">
            {tr('NOC applications submitted and approved, and organizations restoring water bodies across Tamil Nadu.', 'தமிழ்நாடு முழுவதும் சமர்ப்பிக்கப்பட்ட மற்றும் ஒப்புதல் அளிக்கப்பட்ட தடையின்மைச் சான்று விண்ணப்பங்கள், நீர்நிலைகளைப் புனரமைக்கும் அமைப்புகள்.')}
          </p>
        </div>

        {/* Top KPI tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <Tile
            label={tr('Total NOC Applications', 'மொத்த விண்ணப்பங்கள்')}
            value={applications?.total || 0}
            theme="bg-indigo-600 border-indigo-800"
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />}
          />
          <Tile
            label={tr("NOC's Approved", 'ஒப்புதல் அளிக்கப்பட்டவை')}
            value={noc_approved.total}
            theme="bg-emerald-600 border-emerald-800"
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />}
          />
          <Tile
            label={tr('To be Approved', 'ஒப்புதலுக்குக் காத்திருப்பவை')}
            value={applications?.pending || 0}
            theme="bg-amber-500 border-amber-700"
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />}
          />
          <Tile
            label={tr('Works Completed', 'பணி நிறைவடைந்தவை')}
            value={applications?.completed || 0}
            theme="bg-teal-600 border-teal-800"
            icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7M3 20h18" />}
          />
        </div>

        {/* Department-wise summary */}
        <DepartmentSummary rows={applications?.department_summary || []} tr={tr} />

        {/* NGO list */}
        <div className="border border-slate-200 shadow-sm">
          <div className="px-5 py-3 bg-brand-800">
            <h3 className="font-semibold text-white">
              {tr(`Latest ${ngos.recent.length} approved projects across all departments`, `அனைத்துத் துறைகளிலும் சமீபத்தில் ஒப்புதல் பெற்ற ${ngos.recent.length} திட்டங்கள்`)}
            </h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0">
                <tr className="text-left text-white uppercase text-xs tracking-wide bg-brand-600">
                  <th className="px-4 py-3 font-semibold">{tr('Organization / Applicant', 'அமைப்பு / விண்ணப்பதாரர்')}</th>
                  <th className="px-4 py-3 font-semibold">{tr('Water Body', 'நீர்நிலை')}</th>
                  <th className="px-4 py-3 font-semibold">{tr('District', 'மாவட்டம்')}</th>
                  <th className="px-4 py-3 font-semibold">{tr('Dept', 'துறை')}</th>
                  <th className="px-4 py-3 font-semibold">{tr('Status', 'நிலை')}</th>
                </tr>
              </thead>
              <tbody>
                {ngos.recent.map((r) => (
                  <tr key={r.application_id} className="odd:bg-white even:bg-brand-50 hover:bg-brand-100 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-900">{r.ngo_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{r.tank_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{r.district_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 text-xs font-semibold ${deptBadge(r.department_code)}`}>
                        {r.department_code || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 border-l-2 border-emerald-600">
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

const DEPT_BADGES = {
  WRD: 'bg-sky-100 text-sky-800',
  HRCE: 'bg-orange-100 text-orange-800',
  DMA: 'bg-violet-100 text-violet-800',
  'DMA-MUNI': 'bg-violet-100 text-violet-800',
  DTP: 'bg-pink-100 text-pink-800',
  RDPR: 'bg-lime-100 text-lime-800',
  FOREST: 'bg-green-100 text-green-800',
};
const deptBadge = (code) =>
  DEPT_BADGES[String(code || '').toUpperCase().replace('&', '')] || 'bg-slate-100 text-slate-700';

const Tile = ({ label, value, theme, icon }) => (
  <div className={`${theme} border-b-4 text-white shadow-sm p-6 flex items-center justify-between`}>
    <div>
      <div className="text-4xl md:text-5xl font-semibold tracking-tight">
        {Number(value).toLocaleString('en-IN')}
      </div>
      <div className="mt-2 text-sm uppercase tracking-wide text-white/90">{label}</div>
    </div>
    <div className="w-14 h-14 bg-white/15 flex items-center justify-center">
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">{icon}</svg>
    </div>
  </div>
);

// Department order and labels for the summary table (codes as in department_mst).
const SUMMARY_DEPTS = [
  { code: 'WRD',    name: ['Water Resources Department', 'நீர்வளத் துறை'] },
  { code: 'RDPR',   name: ['Rural Development & Panchayat Raj', 'ஊரக வளர்ச்சி மற்றும் ஊராட்சித் துறை'] },
  { code: 'GCC',    name: ['Greater Chennai Corporation', 'பெருநகர சென்னை மாநகராட்சி'] },
  { code: 'HRCE',   name: ['Hindu Religious & Charitable Endowments', 'இந்து சமய அறநிலையத் துறை'] },
  { code: 'FOREST', name: ['Forest Department', 'வனத் துறை'] },
  { code: 'DTP',    name: ['Directorate of Town Panchayats', 'பேரூராட்சிகள் இயக்குநரகம்'] },
  { code: 'DMA',    name: ['Municipal Administration', 'நகராட்சி நிர்வாக இயக்குநரகம்'] },
];

const DepartmentSummary = ({ rows, tr }) => {
  // DMA's department_code is DMA-MUNI; fold any DMA-* rows into DMA.
  const byCode = {};
  rows.forEach((r) => {
    const code = String(r.department_code || '').toUpperCase().startsWith('DMA') ? 'DMA' : String(r.department_code || '').toUpperCase();
    const prev = byCode[code] || { total: 0, approved: 0, pending: 0, completed: 0 };
    byCode[code] = {
      total: prev.total + Number(r.total || 0),
      approved: prev.approved + Number(r.approved || 0),
      pending: prev.pending + Number(r.pending || 0),
      completed: prev.completed + Number(r.completed || 0),
    };
  });
  const lines = SUMMARY_DEPTS.map((d) => ({ ...d, ...(byCode[d.code] || { total: 0, approved: 0, pending: 0, completed: 0 }) }));
  const sum = (k) => lines.reduce((acc, l) => acc + l[k], 0);
  const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
  const cols = [
    ['total', tr('Total Applications', 'மொத்த விண்ணப்பங்கள்')],
    ['approved', tr("Approved NOC's", 'ஒப்புதல் அளிக்கப்பட்டவை')],
    ['pending', tr('To be Approved', 'ஒப்புதலுக்குக் காத்திருப்பவை')],
    ['completed', tr('Completed Works', 'பணி நிறைவடைந்தவை')],
  ];

  return (
    <div className="border border-slate-200 shadow-sm mb-8 overflow-x-auto">
      <div className="px-5 py-3 bg-brand-800">
        <h3 className="font-semibold text-white">{tr('Department-wise Summary', 'துறை வாரியான விவரம்')}</h3>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-brand-600 text-white text-xs uppercase tracking-wide">
            <th className="px-4 py-3 font-semibold text-left">{tr('Department', 'துறை')}</th>
            {cols.map(([k, label]) => (
              <th key={k} className="px-4 py-3 font-semibold text-right">{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.code} className="odd:bg-white even:bg-brand-50 border-b border-slate-100">
              <td className="px-4 py-2.5 font-medium text-slate-800">{tr(...l.name)}</td>
              {cols.map(([k]) => (
                <td key={k} className={`px-4 py-2.5 text-right ${l[k] ? 'font-semibold text-slate-900' : 'text-slate-400'}`}>
                  {l[k] ? fmt(l[k]) : '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-brand-800 text-white">
            <td className="px-4 py-2.5 font-semibold">{tr('Total', 'மொத்தம்')}</td>
            {cols.map(([k]) => (
              <td key={k} className="px-4 py-2.5 text-right font-semibold">{fmt(sum(k))}</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default PortalStats;
